import React, { FC, useEffect, useState, KeyboardEvent } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { useRecoilState, useResetRecoilState } from 'recoil';

import Iqloud_PasswordInput from '@components_iqloud/form/Iqloud_PasswordInput';
import NotificationBar from '@components_iqloud/form/NotificationBar';
import RefTextInput from '@components_iqloud/form/RefTextInput';

import {
  lastSearcheMansionIdAtom,
  currentMansionDisplayNameAtom,
  prefectureAtom,
  cityAtom,
  townAtom,
  propertyTypeAtom,
  lastSearchedTownQueryAtom,
  hasInitializedAtom,
  neighboringSellingContractDataAtom,
  searchResultDataAtom,
} from '@recoil_iqfu/atoms/iqfu_pro/searchQuery';

import { consoleUserType } from '@constants_common';
import { NotificationText } from '@constants_iqfu';
import { OtpChallenge, OtpUserType, useAuth } from '@contexts_common/authContext';
import { getValidatedLoginRedirect } from '@utils_common/getValidatedLoginRedirect';

import styles from './Login.module.scss';

type Props = {
  userType: consoleUserType;
};

const isOtpUserType = (userType: consoleUserType): userType is OtpUserType =>
  userType === 'admin' || userType === 'company';

const Login: FC<Props> = ({ userType }) => {
  const { dashboardLogin, verifyOtp } = useAuth();
  const router = useRouter();
  const [showError, setShowError] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>(NotificationText.loginFailed);
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);
  const [otpCode, setOtpCode] = useState<string>('');
  const [otpRemainingSeconds, setOtpRemainingSeconds] = useState<number>(0);

  const [data, setData] = useState({
    email: '',
    password: '',
  });

  //イクラ不動産プロの検索状態をログイン時にリセットするための関数
  const [iqfuPropropertyType, setIqfuProPropertyType] = useRecoilState(propertyTypeAtom);
  const resetIqfuProLastSearchMansionId = useResetRecoilState(lastSearcheMansionIdAtom);
  const resetIqfuProCurrentMansionDisplayName = useResetRecoilState(currentMansionDisplayNameAtom);
  const resetIqfuProPrefecture = useResetRecoilState(prefectureAtom);
  const resetIqfuProCity = useResetRecoilState(cityAtom);
  const resetIqfuProTown = useResetRecoilState(townAtom);
  const resetIqfuProLastSearchedTownQuery = useResetRecoilState(lastSearchedTownQueryAtom);
  const resetIqfuProHasInitialized = useResetRecoilState(hasInitializedAtom);
  const resetIqfuProSearchResult = useResetRecoilState(searchResultDataAtom);
  const resetIqfuProNeighboringSellingContract = useResetRecoilState(neighboringSellingContractDataAtom);

  const resetIqfuProState = () => {
    setIqfuProPropertyType('landhouse');
    resetIqfuProLastSearchMansionId();
    resetIqfuProCurrentMansionDisplayName();
    resetIqfuProPrefecture();
    resetIqfuProCity();
    resetIqfuProTown();
    resetIqfuProLastSearchedTownQuery();
    resetIqfuProHasInitialized();
    resetIqfuProSearchResult();
    resetIqfuProNeighboringSellingContract();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowError(false);
    const target = e.target;
    const name = target.name;
    const value = target.value;
    setData(() => {
      return { ...data, [name]: value };
    });
  };

  const redirectAfterLogin = (data: any) => {
    //イクラ不動産プロユーザーの不動産会社には、検索ボックスの初期値として会社情報の住所をセットする
    if (userType === 'company' && data?.services?.includes('pro')) {
      resetIqfuProState();
    }

    const redirectUrl = getValidatedLoginRedirect(router.query.redirect, userType, window.location.origin);

    if (redirectUrl) {
      router.push(redirectUrl);
    } else {
      if (userType === 'financial_agency') {
        router.push(`/console/financial_agency/chat/contact`);
      } else {
        router.push(`/console/${userType}/dashboard`);
      }
    }
  };

  const backToPasswordStep = () => {
    setStep('password');
    setOtpChallenge(null);
    setOtpCode('');
    setData({ email: '', password: '' });
    setShowError(false);
  };

  async function onSubmitHandler() {
    setLoading(true);
    // stateからemailとpasswordを取得する
    const { email, password } = data;

    const formData = new FormData();
    formData.append('email', email);
    formData.append('password', password);
    (async () => {
      const result = await dashboardLogin(formData, userType);

      if (result.success) {
        redirectAfterLogin(result.data);
      } else {
        setLoading(false);
        if (result.otpRequired && isOtpUserType(userType)) {
          setOtpChallenge(result.challenge);
          setStep('otp');
          setShowError(false);
        } else {
          const errorCode = 'errorCode' in result ? result.errorCode : undefined;
          setNotificationMessage(
            errorCode === 'otp_send_limit' ? NotificationText.otpRateLimited : NotificationText.loginFailed,
          );
          setShowError(true);
        }
      }
    })();
  }

  async function onSubmitOtpHandler() {
    if (!otpChallenge || !isOtpUserType(userType)) return;
    setLoading(true);
    const result = await verifyOtp(userType, otpChallenge.challengeToken, otpCode);

    if (!result) {
      return;
    }

    if (result.success) {
      redirectAfterLogin(result.data);
      return;
    }

    if (result.status === 410) {
      backToPasswordStep();
      setNotificationMessage(NotificationText.otpChallengeInvalid);
      setShowError(true);
    } else if (result.errorCode === 'otp_invalid') {
      setNotificationMessage(NotificationText.otpInvalid);
      setShowError(true);
    } else if (result.errorCode === 'otp_attempt_limit') {
      setNotificationMessage(NotificationText.otpRateLimited);
      setShowError(true);
    } else if (result.status === 401) {
      // A bare 401 is outside the OTP contract (e.g. the account was locked after the challenge was issued); treat it like a normal login failure.
      backToPasswordStep();
      setNotificationMessage(NotificationText.loginFailed);
      setShowError(true);
    } else {
      setNotificationMessage(NotificationText.error);
      setShowError(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!otpChallenge) return;

    const updateRemainingSeconds = () => {
      const remaining = Math.floor((new Date(otpChallenge.expiresAt).getTime() - Date.now()) / 1000);
      setOtpRemainingSeconds(Math.max(0, remaining));
    };
    updateRemainingSeconds();

    const timer = setInterval(updateRemainingSeconds, 1000);
    return () => clearInterval(timer);
  }, [otpChallenge]);

  const showOtpStep = isOtpUserType(userType) && step === 'otp';

  return (
    <div className={`${styles.login_container} ${userType === 'admin' && styles.admin}`}>
      <NotificationBar
        open={showError}
        toggle={(prevState: boolean) => setShowError(!prevState)}
        close={() => setShowError(false)}
        message={notificationMessage}
        status={'alert'}
      />

      <div className={styles.login_card}>
        {userType === 'admin' && <div className={styles.login_card_title_admin}>管理者用</div>}
        <img src={`/images/iqfu/login_logo.svg`} className={styles.service_logo} alt="イクラ不動産ロゴ" />
        {showOtpStep ? (
          <div className={styles.form}>
            <p className={styles.otp_description}>
              {otpChallenge?.emailHint} 宛に送った 6 桁のコードを入力してください
            </p>
            <RefTextInput
              type="text"
              name="otp"
              placeholder="認証コード"
              className={styles.input}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setShowError(false);
                setOtpCode(e.target.value);
              }}
              onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key == 'Enter' && !loading) onSubmitOtpHandler();
              }}
            />
            <div className={styles.otp_expires}>
              残り {Math.floor(otpRemainingSeconds / 60)}:{String(otpRemainingSeconds % 60).padStart(2, '0')}
            </div>
            <button
              className={`${styles.login_button} ${userType === 'admin' && styles.admin}`}
              onClick={() => onSubmitOtpHandler()}
              disabled={loading}
            >
              ログイン
            </button>
            <button className={styles.otp_retry} onClick={backToPasswordStep} disabled={loading}>
              ログインをやり直す
            </button>
          </div>
        ) : (
          <div className={styles.form}>
            <RefTextInput
              type="text"
              name="email"
              placeholder="メールアドレス"
              className={styles.input}
              onChange={handleInputChange}
              onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key == 'Enter') onSubmitHandler();
              }}
            />
            <Iqloud_PasswordInput
              name="password"
              placeholder="パスワード"
              className={styles.input}
              onChange={handleInputChange}
              onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key == 'Enter') onSubmitHandler();
              }}
            />
            {/* <div className={styles.checkbox_row}>
            <input type="checkbox" className={styles.checkbox} />
            ログイン状態を保持する
          </div> */}
            <button
              className={`${styles.login_button} ${userType === 'admin' && styles.admin}`}
              onClick={() => onSubmitHandler()}
            >
              ログイン
            </button>
            {userType === 'company' && (
              <Link href="/console/company/forgot-password">
                <a className={styles.forget_pass}>パスワードをお忘れの方</a>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
